/*
  THAANI — Kennzahlen-Kreise: Count-up beim Reinscrollen.

  Vanilla JS, kein Framework. Der fertige Wert steht im HTML — das Skript
  zaehlt nur hoch, wenn die Section ins Bild kommt. Animiert werden
  ausschliesslich saubere Einzelwerte (0%, 25 kg, 100%, 23, 2.5x).
  Werte mit mehreren Zahlen oder Tausendertrennern (10'000) bleiben stehen.
  Bei reduzierter Bewegung im Betriebssystem passiert gar nichts.
*/

const THAANI_STATS_DURATION = 1400;
const THAANI_STATS_PATTERN = /^([^0-9]*)(\d+(?:[.,]\d+)?)([^0-9]*)$/;

function thaaniStatsParse(text) {
  if (typeof text !== 'string') return null;

  const match = THAANI_STATS_PATTERN.exec(text.trim());
  if (!match) return null;

  const [, prefix, number, suffix] = match;
  const target = parseFloat(number.replace(',', '.'));
  if (!isFinite(target)) return null;

  const parts = number.split(/[.,]/);
  const separator = number.indexOf(',') > -1 ? ',' : '.';

  return {
    prefix,
    suffix,
    target,
    separator,
    decimals: parts.length > 1 ? parts[1].length : 0,
    text: text.trim(),
  };
}

function thaaniStatsFormat(value, config) {
  let number = value.toFixed(config.decimals);

  if (config.separator === ',') {
    number = number.replace('.', ',');
  }

  return config.prefix + number + config.suffix;
}

class ThaaniStats extends HTMLElement {
  connectedCallback() {
    if (this.dataset.countUp !== 'true') return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.items = Array.from(this.querySelectorAll('[data-thaani-stat-value]'))
      .map((element) => {
        const config = thaaniStatsParse(element.textContent);
        return config ? { element, config } : null;
      })
      .filter(Boolean);

    if (this.items.length === 0) return;

    this.duration = parseInt(this.dataset.duration, 10) || THAANI_STATS_DURATION;
    this.frame = null;

    // Startwert direkt setzen, damit der Zaehler nicht vom Endwert zurueckspringt.
    this.items.forEach(({ element, config }) => {
      element.textContent = thaaniStatsFormat(0, config);
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.observer.disconnect();
        this.observer = null;
        this.run();
      },
      { threshold: 0.35 }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.stop();
  }

  stop() {
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    if (this.fallback) {
      clearTimeout(this.fallback);
      this.fallback = null;
    }
  }

  /* Endwerte setzen, ohne auf einen weiteren Frame zu warten. */
  finish() {
    this.stop();
    this.items.forEach(({ element, config }) => {
      element.textContent = config.text;
    });
  }

  run() {
    const start = performance.now();

    /*
      requestAnimationFrame ruht in Hintergrund-Tabs. Wer waehrend des
      Hochzaehlens wegschaltet, findet die Kennzahlen sonst auf dem Zwischen-
      wert eingefroren vor (etwa "9%" statt "100%") — der Timeout zieht die
      Endwerte notfalls auch ohne Frame nach.
    */
    this.fallback = setTimeout(() => this.finish(), this.duration + 200);

    const step = (now) => {
      const progress = Math.min((now - start) / this.duration, 1);

      if (progress === 1) {
        this.finish();
        return;
      }

      const eased = 1 - Math.pow(1 - progress, 3);

      this.items.forEach(({ element, config }) => {
        element.textContent = thaaniStatsFormat(config.target * eased, config);
      });

      this.frame = requestAnimationFrame(step);
    };

    this.frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('thaani-stats')) {
  customElements.define('thaani-stats', ThaaniStats);
}
